import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Card,
  Container,
  Divider,
  Grid,
  IconButton,
  useTheme
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import axiosConfig from '../../../Resources/axiosConfig';
import { AxiosError, AxiosResponse } from 'axios/index';
import routes from '../../../Resources/routes.json';
import { useNavigate } from 'react-router-dom';
import { TokenContext } from '../../../contexts/tokenContext';
import { ErrorMessage } from '../../../interfaces/S3Types';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

interface LogProps {
  errorLoadingS3Data: boolean;
  id: number;
}

function Log(props: LogProps) {
  const theme = useTheme();
  const searchParams = new URLSearchParams(location.search);
  const openModal = searchParams.get('open');

  const [warnings, setWarnings] = useState<ErrorMessage[]>([]);
  const [errors, setErrors] = useState<ErrorMessage[]>([]);
  const [errorButtonPressed, setErrorButtonPressed] = useState(
    openModal === 'error' ? true : false
  );
  const [warningButtonPressed, setWarningButtonPressed] = useState(
    openModal === 'warning' ? true : false
  );
  const [updateCount, setUpdateCount] = useState(0);
  const navigate = useNavigate();
  const tokencontext = useContext(TokenContext);
  const { removeToken } = tokencontext;

  useEffect(() => {
    axiosConfig
      .get(`/GetAllErrorsForInstallation?id=${props.id}`)
      .then((res: AxiosResponse<ErrorMessage[]>) => {
        setErrors(res.data);
      })
      .catch((err: AxiosError) => {
        if (err.response && err.response.status == 401) {
          removeToken();
          navigate(routes.login);
        }
      });

    axiosConfig
      .get(`/GetAllWarningsForInstallation?id=${props.id}`)
      .then((res: AxiosResponse<ErrorMessage[]>) => {
        setWarnings(res.data);
      })
      .catch((err: AxiosError) => {
        if (err.response && err.response.status == 401) {
          removeToken();
          navigate(routes.login);
        }
      });
  }, [updateCount]);

  const handleErrorButtonPressed = () => {
    setErrorButtonPressed(!errorButtonPressed);
  };

  const handleWarningButtonPressed = () => {
    setWarningButtonPressed(!warningButtonPressed);
  };

  const handleErrorAcknowledgeButtonPressed = (errorId: number) => {
    axiosConfig
      .post(`/AcknowledgeError?id=${errorId}`)
      .then((res: AxiosResponse<ErrorMessage[]>) => {
        setUpdateCount(updateCount + 1);
      })
      .catch((err: AxiosError) => {
        if (err.response && err.response.status == 401) {
          removeToken();
          navigate(routes.login);
        }
      });
  };

  const handleWarningAcknowledgeButtonPressed = (warningId: number) => {
    axiosConfig
      .post(`/AcknowledgeWarning?id=${warningId}`)
      .then((res: AxiosResponse<ErrorMessage[]>) => {
        setUpdateCount(updateCount + 1);
      })
      .catch((err: AxiosError) => {
        if (err.response && err.response.status == 401) {
          removeToken();
          navigate(routes.login);
        }
      });
  };

  const warningDescriptionMap: { [key: string]: string } = {
    "TaM1": "TaM1: BMS temperature high",
    "TbM1": "TbM1: Battery temperature high",
    "VBm1": "VBm1: Bus voltage low",
    "VBM1": "VBM1: Bus voltage high",
    "IDM1": "IDM1: Discharge current high",
    "vsm1": "vsm1: String voltage low",
    "vsM1": "vsM1: String voltage high",
    "iCM1": "iCM1: Charge current high",
    "iDM1": "iDM1: Discharge current high",
    "MID1": "MID1: String voltages unbalanced",
    "BLPW": "BLPW: Not enough charging power on bus",
    "CCBF": "CCBF: Internal charger hardware failure",
    "Ah_W": "Ah_W: String SOC low",
    "MPMM": "MPMM: Midpoint wiring problem",
    "TCdi": "TCdi: Temperature difference between strings high",
    "LMPW": "LMPW: String voltages unbalance warning",
    "TOCW": "TOCW: Top of Charge requested"
  };

  const errorDescriptionMap: { [key: string]: string } = {
    "Tam": "Tam: Recoverable, BMS temperature too low",
    "TaM2": "TaM2: Recoverable, BMS temperature too high",
    "Tbm": "Tbm: Recoverable, Battery temperature too low",
    "TbM2": "TbM2: Recoverable, Battery temperature too high",
    "VBm2": "VBm2: Recoverable, Recoverable: Bus voltage too low",
    "VBM2": "VBM2: Recoverable,Recoverable: Bus voltage too high",
    "IDM2": "IDM2: Recoverable, Discharge current too high",
    "ISOB": "ISOB: Unrecoverable, Electrical insulation failure",
    "MSWE": "MSWE: Unrecoverable, Main switch failure",
    "FUSE": "FUSE: Unrecoverable, Main fuse blown",
    "HTRE": "HTRE: Recoverable, Battery failed to warm up",
    "TCPE": "TCPE: Unrecoverable, Temperature sensor failure",
    "STRE": "STRE: Recoverable, Voltage measurement circuit fails",
    "CME": "CME: Recoverable, Current sensor failure",
    "HWFL": "HWFL: Recoverable, BMS hardware failure",
    "HWEM": "HWEM: Recoverable, Hardware protection tripped",
    "ThM": "ThM: Recoverable, Heatsink temperature too high",
    "vsm2": "vsm2: Unrecoverable, Low string voltage failure",
    "vsM2": "vsM2: Recoverable, String voltage too high",
    "iCM2": "iCM2: Unrecoverable, Charge current too high",
    "iDM2": "iDM2: Recoverable, Discharge current too high",
    "MID2": "MID2: Recoverable, String voltage unbalance too high",
    "HTFS": "HTFS: Recoverable, Unrecoverable: Heater Fuse Blown",
    "DATA": "DATA: Recoverable, Unrecoverable: Parameters out of range",
    "LMPA": "LMPA: Unrecoverable, String voltages unbalance alarm",
    "HEBT": "HEBT: Recoverable, oss of heartbeat"
  };

  return (
    <Container maxWidth="xl">
      <Grid container>
        <Grid item xs={12} md={12}>
          <Button
            variant="contained"
            onClick={handleErrorButtonPressed}
            sx={{ marginTop: '20px', backgroundColor: errorButtonPressed ? '#808080' : 'default'}}
          >
            <FormattedMessage id="Show Errors" defaultMessage="Show Errors" />
          </Button>

          {errorButtonPressed && errors.length > 0 && (
            <Card sx={{ marginTop: '10px' }}>
              <Divider />
              <div>
                <div
                  style={{
                    height: '40px',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="type" defaultMessage="Type" />
                    </Typography>
                  </div>

                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage
                        id="description"
                        defaultMessage="Description"
                      />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="device" defaultMessage="Device" />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="date" defaultMessage="Date" />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="time" defaultMessage="Time" />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="seen" defaultMessage="Seen" />
                    </Typography>
                  </div>
                </div>
                <Divider />
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {errors.map((error, index) => (
                    <>
                      <Divider />
                      <div
                        key={index}
                        style={{
                          height: '40px',
                          marginBottom: '10px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            marginTop: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <ErrorIcon
                            sx={{
                              color: 'red',
                              width: 25,
                              height: 25
                            }}
                          />
                        </div>

                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {errorDescriptionMap[error.description] || error.description}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {error.deviceCreatedTheMessage}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {error.date}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {error.time}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={error.seen}
                                onChange={() =>
                                  error.seen === false &&
                                  handleErrorAcknowledgeButtonPressed(error.id)
                                }
                                sx={{
                                  marginLeft: '25px'
                                }}
                              />
                            }
                            label=""
                            sx={{ marginTop: 1 }}
                          />
                        </div>
                      </div>
                    </>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {!props.errorLoadingS3Data &&
            errorButtonPressed &&
            errors.length == 0 && (
              <Alert
                severity="error"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '20px'
                }}
              >
                <FormattedMessage
                  id="noerrors"
                  defaultMessage="There are no errors"
                />
                <IconButton
                  color="inherit"
                  size="small"
                  sx={{ marginLeft: '4px' }}
                ></IconButton>
              </Alert>
            )}
        </Grid>

        <Grid item xs={12} md={12}>
          <Button
            variant="contained"
            onClick={handleWarningButtonPressed}
                       sx={{ marginTop: '20px', backgroundColor: warningButtonPressed ? '#808080' : 'default'}}

          >
            <FormattedMessage
              id="Show Warnings"
              defaultMessage="Show Warnings"
            />
          </Button>

          {warningButtonPressed && warnings.length > 0 && (
            <Card sx={{ marginTop: '10px' }}>
              <Divider />
              <div>
                <div
                  style={{
                    height: '40px',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="type" defaultMessage="Type" />
                    </Typography>
                  </div>

                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage
                        id="description"
                        defaultMessage="Description"
                      />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="device" defaultMessage="Device" />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="date" defaultMessage="Date" />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="time" defaultMessage="Time" />
                    </Typography>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      marginTop: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="dimgrey"
                      fontWeight="bold"
                      fontSize="1rem"
                      gutterBottom
                      noWrap
                    >
                      <FormattedMessage id="seen" defaultMessage="Seen" />
                    </Typography>
                  </div>
                </div>
                <Divider />
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {warnings.map((warning, index) => (
                    <>
                      <Divider />
                      <div
                        key={index}
                        style={{
                          height: '40px',
                          marginBottom: '10px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <div
                          style={{
                            flex: 1,
                            marginTop: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <WarningIcon
                            sx={{
                              color: 'orange',
                              width: 25,
                              height: 25
                            }}
                          />
                        </div>

                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {warningDescriptionMap[warning.description] || warning.description}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {warning.deviceCreatedTheMessage}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {warning.date}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            marginTop: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                          >
                            {warning.time}
                          </Typography>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={warning.seen}
                                onChange={() =>
                                  warning.seen === false &&
                                  handleWarningAcknowledgeButtonPressed(
                                    warning.id
                                  )
                                }
                                sx={{
                                  marginLeft: '25px'
                                }}
                              />
                            }
                            label=""
                            sx={{ marginTop: 1 }}
                          />
                        </div>
                      </div>
                    </>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {!props.errorLoadingS3Data &&
            warningButtonPressed &&
            warnings.length == 0 && (
              <Alert
                severity="error"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  //marginBottom: '20px'
                  marginTop: '20px'
                }}
              >
                <FormattedMessage
                  id="nowarnings"
                  defaultMessage="There are no warnings"
                />
                <IconButton
                  color="inherit"
                  size="small"
                  sx={{ marginLeft: '4px' }}
                ></IconButton>
              </Alert>
            )}
        </Grid>

        <Grid item xs={12} md={12} style={{ marginBottom: '20px' }}>
          {props.errorLoadingS3Data && (
            <Alert
              severity="error"
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '20px'
              }}
            >
              <FormattedMessage
                id="cannotloadloggingdata"
                defaultMessage="Cannot load logging data"
              />
              <IconButton
                color="inherit"
                size="small"
                sx={{ marginLeft: '4px' }}
              ></IconButton>
            </Alert>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}

export default Log;
